import { useRef, useState } from "react";
import { Upload, X, ImageIcon, AlertCircle } from "lucide-react";
import { api, formatApiError } from "@/api";

/**
 * Combined "Attach" + "paste URL" field.
 *
 * Props:
 *   value    — current image URL (string, "" when empty)
 *   onChange — (nextUrl) => void
 *   label    — label shown above the field
 *   testId   — root data-testid prefix (adds -input / -attach / -clear / -preview)
 */
export default function ImageUploadField({ value, onChange, label = "Image", testId = "image" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const openPicker = () => {
    if (uploading) return;
    setError("");
    fileRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/admin/upload", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Upload failed. Try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = ""; // allow re-selecting same file
    }
  };

  const clear = () => {
    onChange("");
    setError("");
  };

  return (
    <label className="flex flex-col gap-2 md:col-span-2">
      <span className="font-mono-label text-[var(--k-muted)]">{label}</span>

      <div className="flex items-start gap-4">
        <div
          className="w-24 h-24 shrink-0 rounded-lg border border-dashed border-[var(--k-line)] bg-[var(--k-bg-2)] overflow-hidden flex items-center justify-center"
          data-testid={`${testId}-preview`}
        >
          {value ? (
            <img src={value} alt="Preview" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="w-6 h-6 text-[var(--k-muted)]" strokeWidth={1.4} />
          )}
        </div>

        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openPicker}
              disabled={uploading}
              className="btn-outline-green !text-[0.55rem] !py-2 !px-3"
              data-testid={`${testId}-attach`}
            >
              <Upload className="w-3.5 h-3.5" />
              {uploading ? "Uploading…" : value ? "Replace" : "Attach Image"}
            </button>
            {value && (
              <button
                type="button"
                onClick={clear}
                className="btn-outline-green !text-[0.55rem] !py-2 !px-3 !border-red-300 !text-red-500 hover:!bg-red-50"
                data-testid={`${testId}-clear`}
              >
                <X className="w-3.5 h-3.5" /> Remove
              </button>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleFile}
            className="hidden"
            data-testid={`${testId}-file`}
          />
          {value && (
            <div
              className="font-mono-label text-[var(--k-muted)] text-[0.55rem] truncate"
              data-testid={`${testId}-filename`}
              title={value}
            >
              {value.split("/").pop()}
            </div>
          )}
          {error && (
            <div className="flex items-start gap-2 text-red-600 text-xs" data-testid={`${testId}-error`}>
              <AlertCircle className="w-3.5 h-3.5 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          <div className="font-mono-label text-[var(--k-muted)] text-[0.55rem]">
            JPG · PNG · WEBP · GIF · max 8 MB
          </div>
        </div>
      </div>
    </label>
  );
}
